// Report Composer — approvalBlock special 위젯 (공유, P1-2)
//
// Composer 템플릿의 `special: 'approvalBlock'` 노드를 실제 결재란으로 렌더한다.
// 결재란 트리는 ApprovalLineDisplay(→ variant별 layout)를 재사용한다. 입력 데이터는
// 호스트(web)가 RenderData.approval에 ApprovalBlockData 형태로 넣어둔 것을 읽는다.
//
// P1-2 공유화: web 전용 타입(report-renderer/smsContentAdapter) 의존을 제거하고
// 공유 표시 타입(ApprovalLineItem/ApprovalSignatureDisplay) 기반 ApprovalBlockData만 받는다.
// web adapter가 자기 타입 → ApprovalBlockData로 변환해 주입한다.

import type { SpecialComponentProps } from '../types';
import { ApprovalLineDisplay } from './ApprovalLineDisplay';
import type { ApprovalBlockData, ApprovalBlockConfig } from './types';
import { StepperLayout } from './layouts/StepperLayout';
import { DEFAULT_APPROVAL_CONFIG } from '../templateOps';

/** node.props.approvalConfig가 객체면 ApprovalBlockConfig로 취급(부분 검증). 아니면 undefined. */
function asApprovalConfig(v: unknown): ApprovalBlockConfig | undefined {
  return v && typeof v === 'object' ? (v as ApprovalBlockConfig) : undefined;
}

/**
 * Composer 결재란 전용 — 단계명란/이름란 글자색이 config에 미지정일 때 Composer 기본색(#111)을 폴백한다.
 * admin 색 input은 미지정 시 defaultColor(#111)를 표시하는데, 실제 렌더(StepperLayout→fontStyleToCss)는
 * 미지정 시 CSS 기본 #000을 상속해 표시·실제가 어긋났다. 여기서 색만 채워 표시=실제=초기값(#111)을 일치시킨다.
 * 이 폴백은 ApprovalBlockSpecial(Composer special 경로) 한정 — `StepperLayout` 자체는 DEFAULT_APPROVAL_CONFIG와
 * 무관하다. 색 외 폰트 속성(크기/굵기 등)은 그대로 둔다.
 */
function withDefaultFontColors(config: ApprovalBlockConfig | undefined): ApprovalBlockConfig {
  return {
    ...config,
    stepLabelFont: {
      ...config?.stepLabelFont,
      color: config?.stepLabelFont?.color ?? DEFAULT_APPROVAL_CONFIG.stepLabelFont?.color,
    },
    nameFont: {
      ...config?.nameFont,
      color: config?.nameFont?.color ?? DEFAULT_APPROVAL_CONFIG.nameFont?.color,
    },
  };
}

export function ApprovalBlockSpecial({ node, data }: SpecialComponentProps) {
  const approval = (data.approval as ApprovalBlockData | undefined) ?? {};

  // Composer 결재란은 항상 StepperLayout으로 고정한다(variant 배선 제거).
  // 출력/치수/폰트는 approvalConfig(구성 옵션, stepper 기반)로 자유 설정한다.
  // (smsTable/dilBoxes 레이아웃은 네이티브 보고서 type→layout 매핑 전용으로 보존됨.)
  const layout = StepperLayout;

  // 구성 옵션(P2a-3) — stepper 기반 옵션. 단계명란/이름란 글자색은 미지정 시 Composer 기본(#111) 폴백.
  const config = withDefaultFontColors(asApprovalConfig(node.props?.approvalConfig));

  const approvalLines = approval.approvalLines ?? [];
  const approvalSignatures = approval.approvalSignatures ?? [];

  // 정의/서명이 전혀 없어도 "담당" 칸 1개가 항상 표시되어야 한다.
  // buildMergedApprovalItems는 둘 다 비면 빈 배열(미렌더)을 반환하므로,
  // 이 경우 author 단계 1개를 합성해 담당 칸을 보장한다(원본 print과 동일).
  const linesForDisplay =
    approvalLines.length === 0 && approvalSignatures.length === 0
      ? [{ approvalLevel: 1, label: '담당', sourceType: 'author' as const }]
      : approvalLines;

  return (
    <ApprovalLineDisplay
      layout={layout}
      approvalLines={linesForDisplay}
      approvalSignatures={approvalSignatures}
      authorSignerName={approval.authorSignerName}
      authorSignatureData={approval.authorSignatureData}
      offlineSignature={approval.offlineSignature}
      config={config}
    />
  );
}
